<template>
  <div class="details">
    <b-row class="p-10 pt-20">
      <b-col cols="1"></b-col>
      <b-col cols="10" v-if="surveyDetails && surveyDetails.length > 0">
        <div class="pt-15 card inner-card br-2" v-for="(survey, index) in surveyDetails.filter(surveyDetail => surveyDetail.id === selectedSurvey.id)" :key="index">
          <div class="pl-20 pb-15">
            <b-row>
              <b-col cols="3" class="p-0">
                {{ survey.title }}
              </b-col>
            </b-row>
          </div>
          <div class="pl-20 pb-15">
            <div v-if="survey.therapeuticArea">
              <div class="header">
                Description
              </div>
              <div class="content">
                {{ survey.nickName }}
              </div>
            </div>
          </div>
          <div class="pl-20 pb-15">
            <div class="pb-10" v-if="survey.therapeuticArea">
              <div class="header">
                Therapeutic area
              </div>
              <div class="content">
                {{ survey.therapeuticArea }}
              </div>
            </div>
            <div class="pb-10" v-if="survey.therapeuticIndication">
              <div class="header">
                Therapeutic indication
              </div>
              <div class="content">
                {{ survey.therapeuticIndication }}
              </div>
            </div>
            <div class="pb-10" v-if="survey.timeToComplete">
              <div class="header">
                Approximate time to complete
              </div>
              <div class="content" v-if="survey.timeToComplete">{{ survey.timeToComplete > 1 ? survey.timeToComplete + " mins" : survey.timeToComplete + " min" }}</div>
              <div class="content" v-if="!survey.timeToComplete">N/A</div>
            </div>
            <b-row class="pb-10" v-if="survey.scheduler && survey.scheduler.milestones">
              <b-col v-if="survey.scheduler && survey.scheduler.milestones && survey.scheduler.milestones.length > 0" class="p-0">
                <div class="header">
                  To be completed at milestones
                </div>
                <div class="content">
                  <span v-for="(milestone, index) in survey.scheduler.milestones" :key="index">
                    {{ displayScreeningMilestones(milestone) }}
                    <span v-if="index + 1 < survey.scheduler.milestones.length">,</span>
                  </span>
                </div>
              </b-col>
              <b-col v-if="survey.scheduler.completionWindows">
                <div class="header">
                  Completion windows
                </div>
                <div class="content">{{ Object.values(survey.scheduler.completionWindows).join(", ") }}</div>
              </b-col>
            </b-row>
            <b-row class="pb-10" v-if="survey.scheduler && survey.scheduler.frequency">
              <b-col class="p-0">
                <div class="header">
                  Frequency
                </div>
                <div class="content">
                  {{ survey.scheduler.frequency.title }}
                </div>
              </b-col>
              <b-col v-if="survey.scheduler.completionWindow" class="p-0">
                <div class="header">
                  Completion window
                </div>
                <div class="content">{{ survey.scheduler.completionWindow > 1 ? survey.scheduler.completionWindow + " days" : survey.scheduler.completionWindow + " day" }} </div>
              </b-col>
            </b-row>
            <b-row class="pb-10" v-if="survey.scheduler && survey.scheduler.isActivityOptional">
              <b-col class="p-0">
                <div class="header">
                  Activity is optional and will be excluded from the compliance report.
                </div>
                <div class="content">
                  Yes
                </div>
              </b-col>
            </b-row>
            <b-row class="pb-10" v-if="survey.scheduler && survey.scheduler.allowMultipleTimes">
              <b-col class="p-0">
                <div class="header">
                  Allow participants to repeat survey each time it is present
                </div>
                <div class="content">
                  Yes
                </div>
              </b-col>
            </b-row>
            <b-row class="pb-10" v-if="survey.watchEnabled">
              <b-col class="p-0">
                <div class="header">
                  Allow survey to be completed on an Apple Watch. Limited question types if selected.
                </div>
                <div class="content">
                  Yes
                </div>
              </b-col>
            </b-row>
            <b-row class="pb-10">
              <b-col cols="12" class="header p-0"> Email Recipient: </b-col>
              <div class="content">
                <div class="header">Questions and answers included: </div>
                <div v-if="questions.length > 0">
                  <div class="content" v-for="(question, index) in questions" :key="index">• {{ question }}</div>
                </div>
                <div class="content" v-if="questions.length == 0">• None</div>
              </div>
            </b-row>

            <div class="pb-10" v-if="questions.length !== 0">
              <div class="header">Sent to:</div>
              <div class="content" v-if="isRecipientFlagSet">
                <b>Site Team</b>
              </div>
              <div v-if="emails.length > 0">
                <div class="content" v-for="(item, index) in emails" :key="index">
                  {{ item.email }}
                </div>
              </div>
              <div v-if="emails.length == 0 && !isRecipientFlagSet">N/A</div>
            </div>
            <b-row class="pb-10" v-if="survey.scheduler && survey.scheduler.duration">
              <b-col cols="12" class="header p-0">
                Scheduled as follows
              </b-col>
              <b-col class="p-0" cols="4" v-for="(item, index) in survey.scheduler.duration" :key="index">
                <b-row class="p-0">
                  <b-col class="p-0" cols="3" v-if="item.startDay !== null && item.startDay !== undefined">
                    <div class="content">
                      Start Day
                    </div>
                    <div class="content">
                      {{ getDay(studyData.screeningMilestoneAllowed, studyData.screeningDuration, item.startDay) }}
                    </div>
                  </b-col>
                  <b-col class="p-0" cols="3" v-if="item.endDay !== null && item.endDay !== undefined">
                    <div class="content">
                      End Day
                    </div>
                    <div class="content">
                      {{ getDay(studyData.screeningMilestoneAllowed, studyData.screeningDuration, item.endDay) }}
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row class="pb-10" v-if="survey.scheduler && !(survey.scheduler && survey.scheduler.milestones && survey.scheduler.milestones.length > 0)">
              <b-col class="p-0" v-if="survey.scheduler.allowUntillDuration">
                <div class="header">
                  Available until the end of participant's study duration
                </div>
                <div class="content">
                  Yes
                </div>
              </b-col>
            </b-row>
            <b-row class="pb-10" v-if="survey.scheduler && survey.scheduler.reminders">
              <b-col cols="12" class="header p-0">
                Reminders
              </b-col>
              <b-col class="p-0" cols="12">
                <b-row class="p-0">
                  <b-col class="p-0" cols="2">
                    <div class="header">
                      Day
                    </div>
                  </b-col>
                  <b-col class="p-0" cols="6">
                    <div class="header">
                      Message
                    </div>
                  </b-col>
                  <b-col class="p-0" cols="2">
                    <div class="header">
                      Time
                    </div>
                  </b-col>
                  <b-col class="p-0" cols="2">
                    <div class="header">
                      Notification type
                    </div>
                  </b-col>
                </b-row>
                <b-row class="p-0" v-for="(item, index) in survey.scheduler.reminders" :key="index">
                  <b-col class="p-0" cols="2">
                    <div class="content">
                      {{ item.day }}
                    </div>
                  </b-col>
                  <b-col class="p-0" cols="6">
                    <div class="content" v-if="item.message">
                      {{ item.message }}
                    </div>
                  </b-col>
                  <b-col class="p-0" cols="2">
                    <div class="content" v-if="item.time">
                      {{ item.time }}
                    </div>
                  </b-col>
                  <b-col class="p-0" cols="2">
                    <div class="content">
                      <span>Mobile</span>
                      <span v-if="item.emailToggle">, Email</span>
                      <span v-if="item.smsToggle">, SMS Text</span>
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row class="content pb-10" v-if="survey.prePopulationEnabled">
              Show participant's their previous survey answers, if applicable. Only applies for this survey
            </b-row>
          </div>
          <div class="pl-10 pb-15" v-if="survey.questions.length > 0">
            <div v-for="(question, index) in survey.questions" :key="index">
              <div class="card read_only_question">
                <div class="row">
                  <div class="col-12 p-0">
                    <div v-if="question.type === 'SurveyInstruction' || question.type === 'SurveyCompletion' || question.type === 'GPSkin' || question.type === 'ImageCapture' || question.type === 'SkinAnalyser'">
                      <div v-if="question.templateImageUrl">
                        <div class="row">
                          <div class="col-lg-6 col-md-6">
                            <h5 class="instructionTitle fs-14" v-html="question.formattedTitle"></h5>
                            <p class="question_body fs-12" v-html="question.information"></p>
                            <div class="row">
                              <div class="col-lg-6 col-md-6 pl-0 pr-20" v-if="question.imageUrl">
                                <img class="img_size" :src="question.imageUrl" alt="Image" />
                              </div>
                            </div>
                          </div>
                          <div class="col-lg-6 col-md-6">
                            <h5 class="instructionTitle fs-14">
                              Template Image
                            </h5>
                            <div class="row">
                              <div class="col-lg-6 col-md-6 pl-0 pr-20" v-if="question.templateImageUrl">
                                <img class="img_size" :src="question.templateImageUrl" alt="Image" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else>
                        <h5 class="instructionTitle fs-14" v-html="question.formattedTitle"></h5>
                        <p class="question_body fs-12" v-html="question.information"></p>
                        <div class="row">
                          <div class="col-lg-6 col-md-6 pl-0 pr-20" v-if="question.imageUrl">
                            <img class="img_size" :src="question.imageUrl" alt="Image" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="question.type !== 'SurveyInstruction' && question.type !== 'SurveyCompletion' && question.type !== 'GPSkin' && question.type !== 'ImageCapture' && question.type !== 'SkinAnalyser'">
                      <div class="question-section">
                        <h5 class="font-style fs-16">Screen {{ question.questionNum }}</h5>
                        <p class="question_body">
                          <span v-html="question.formattedBody"></span>
                          <span v-if="question.characterLimited">(Limit - {{ question.textLimit }})</span>
                        </p>
                        <div v-if="question.hasBodyImage">
                          <div v-for="(img, index) in question.bodyImages" :key="index">
                            <img :src="img.imageUrl" alt="" class="wid300" />
                          </div>
                        </div>
                        <label class="fw-normal display-block mb-15">
                          <span class="position_relative">Allow skipping: {{ question.isSkippable }}</span>
                        </label>
                      </div>
                      <div class="answer-section">
                        <div class="option-section" v-if="question.type.toLowerCase() === 'boolean'">
                          <div v-for="(option, index) in question.options" :key="index">
                            <p class="radio-option">
                              <input type="radio" name="boolean_type" disabled />
                              <label>{{ option.formattedBody }}</label>
                            </p>
                            <div v-if="questionIds[option.nextQuestion]">
                              <label class="pl-5"
                                >If this option is selected, skips to screen <b>{{ questionIds[option.nextQuestion] }}</b></label
                              >
                            </div>
                          </div>
                        </div>
                        <div class="option-section pad-top20" v-if="question.type.toLowerCase() === 'numberscale'">
                          <ds-slider
                            v-if="!question.verticalScale"
                            ref="slider"
                            :value="question.minValue"
                            :piecewise="true"
                            direction="horizontal"
                            class="horizontal-vue-slider pl-20"
                            :min="question.minValue"
                            :max="question.maxValue"
                            :disabled="true"
                            :piecewiseLabel="true"
                            :piecewiseStyle="piecewiseStyle"
                            :interval="question.partitions"
                            :piecewiseActiveStyle="piecewiseActiveStyle"
                            :labelActiveStyle="labelActiveStyle"
                            :hideScale="question.hideScale"
                          ></ds-slider>
                          <ds-slider
                            v-if="question.verticalScale"
                            ref="slider"
                            :value="question.minValue"
                            :piecewise="true"
                            direction="vertical"
                            class="vertical-vue-slider pl-20"
                            width="2"
                            :min="question.minValue"
                            :max="question.maxValue"
                            :disabled="true"
                            :piecewiseLabel="true"
                            :piecewiseStyle="piecewiseStyle"
                            :interval="question.partitions"
                            :piecewiseActiveStyle="piecewiseActiveStyle"
                            :labelActiveStyle="labelActiveStyle"
                            :hideScale="question.hideScale"
                          ></ds-slider>
                        </div>
                        <div class="option-section pad-top20 step-type-custom-scale" v-if="question.type.toLowerCase() === 'customscale'">
                          <ds-slider
                            v-if="!question.verticalScale"
                            ref="slider"
                            :value="question.formattedLabels[0]"
                            :piecewise="true"
                            width="1"
                            direction="horizontal"
                            class="horizontal-vue-slider pl-20"
                            :data="question.formattedLabels"
                            :disabled="true"
                            :piecewiseLabel="true"
                            :piecewiseStyle="piecewiseStyle"
                            :piecewiseActiveStyle="piecewiseActiveStyle"
                            :labelActiveStyle="labelActiveStyle"
                          ></ds-slider>
                          <ds-slider v-if="question.verticalScale" ref="slider" :value="question.formattedLabels[0]" :piecewise="true" width="2" direction="vertical" class="vertical-vue-slider pl-20" :data="question.formattedLabels" :disabled="true" :piecewiseLabel="true" :piecewiseStyle="piecewiseStyle" :piecewiseActiveStyle="piecewiseActiveStyle" :labelActiveStyle="labelActiveStyle"></ds-slider>
                        </div>
                        <div class="option-section" v-if="question.type.toLowerCase() === 'text'">
                          <input type="text" class="input-text readonly" placeholder="" readonly />
                        </div>
                        <div class="option-section" v-if="question.type.toLowerCase() === 'datetime'">
                          <div class="col-sm-3 p-0" v-if="question.showDate">
                            <input type="text" class="input-text readonly" placeholder="" v-model="question.dateFormat" readonly />
                          </div>
                          <div class="col-sm-3 pt-3 pl-0" v-if="question.showTime">
                            <span v-if="question.timeFormat == '12'">
                              <input type="text" class="input-text readonly" placeholder="" value="--:-- AM" readonly />
                            </span>
                            <span v-if="question.timeFormat == '24'">
                              <input type="text" class="input-text readonly" placeholder="" value="--:--" readonly />
                            </span>
                          </div>
                        </div>
                        <div class="option-section" v-if="question.type.toLowerCase() === 'number'">
                          <div class="">
                            <input type="text" class="input-text readonly" placeholder="" readonly />
                            <div>
                              <label class="fw-normal display-block pt-10" v-if="question.hasUnits">
                                <span class="position_relative">Answer label: {{ question.units }}</span>
                              </label>
                              <label class="fw-normal display-block" v-if="question.hasMinMax">
                                <span class="position_relative"
                                  >Min/max value
                                  <span class="position_relative">{{ question.minValue }}</span>
                                  <span class="position_relative"> to {{ question.maxValue }}</span>
                                </span>
                              </label>
                              <label class="fw-normal display-block" v-if="question.allowDecimals">
                                <span class="position_relative">Allow decimals: {{ question.allowDecimals }}</span>
                              </label>
                            </div>
                          </div>
                        </div>
                        <div class="option-section" v-if="question.type.toLowerCase() === 'valuepicker'">
                          <div class="">
                            <input type="number" class="input-text readonly" placeholder="" readonly />
                          </div>
                        </div>
                        <div class="option-section" v-if="question.type.toLowerCase() === 'imagechoice'">
                          <div class=""></div>
                        </div>
                        <div class="option-section" v-if="question.type.toLowerCase() === 'email'">
                          <div class="">
                            <input type="email" class="input-text readonly" placeholder="" readonly />
                          </div>
                        </div>
                        <div class="option-section" v-if="question.type.toLowerCase() === 'location'">
                          <div class="">
                            <input type="text" class="input-text readonly" placeholder="" readonly />
                          </div>
                        </div>
                        <div class="option-section" v-if="question.type.toLowerCase() === 'multichoice'">
                          <div v-if="question.subType.toLowerCase() === 'multi'">
                            <div v-for="(option, index) in question.options" :key="index">
                              <div v-if="option.enabled">
                                <label> <input type="checkbox" disabled />&nbsp;<span v-html="splitPTagfromContent(option.formattedBody)"></span> </label>
                                <div v-if="option.imageUrl">
                                  <img :src="option.imageUrl" alt="" />
                                </div>
                                <div v-if="questionIds[option.nextQuestion]">
                                  <label class="pl-5"
                                    >If this option is selected, skips to screen <b>{{ questionIds[option.nextQuestion] }}</b></label
                                  >
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="question.subType.toLowerCase() === 'single'">
                            <div v-for="(option, index) in question.options" :key="index">
                              <div v-if="option.enabled">
                                <label> <input type="radio" disabled />&nbsp;<span v-html="splitPTagfromContent(option.formattedBody)"></span> </label>
                                <div v-if="option.imageUrl">
                                  <img :src="option.imageUrl" alt="" class="" />
                                </div>
                                <div v-if="questionIds[option.nextQuestion]">
                                  <label class="pl-5"
                                    >If this option is selected, skips to screen <b>{{ questionIds[option.nextQuestion] }}</b></label
                                  >
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="option-section lang-img-options" v-if="question.type.toLowerCase() === 'image'">
                          <div class="row justify-content-start">
                            <div class="col-auto pl-0 pr-0" v-for="(langOptionobj, loopIndex) in question.languageImageOptions" :key="loopIndex">
                              <div class="language-name">
                                {{ langOptionobj.languageName }}
                              </div>
                              <div class="form-group clearfix" v-for="(option, optionIndex) in langOptionobj.options" :key="optionIndex">
                                <div v-if="option.enabled">
                                  <span v-if="question.subType.toLowerCase() === 'multi'">
                                    <input type="checkbox" id="checkbox" disabled />
                                  </span>
                                  <span v-else>
                                    <input type="radio" id="radio" disabled />
                                  </span>
                                  <img class="col-lg-2 img-size" :src="option.imageUrl" alt="Image" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import dsSlider from "../../../lib/ds-slider-component";
import toastr from "toastr";
import QlVideoUploadService from "@/services/ql-video-upload";
import localStorage from "@/services/localStorage";
import studyDetailMixin from "@/components/study-details/study-detail-mixin";
/* 
IMPORTANT
Any changes made in this file should also be made in the survey.vue which is inside the study-details-pdf folder
 */
const MODULE_NAME = "survey";
export default {
  name: "survey-details",
  data() {
    return {
      studyData: {},
      surveyDetails: [],
      selectedSurvey: {},
      studyId: this.$route.params.studyId,
      surveyId: this.$route.params.surveyId,
      piecewiseActiveStyle: {
        backgroundColor: "#3498db"
      },
      labelActiveStyle: {
        color: "#3498db"
      },
      piecewiseStyle: {
        backgroundColor: "#ccc",
        visibility: "visible",
        width: "12px",
        height: "12px"
      },
      questionIds: {},
      questions: [],
      emails: [],
      isRecipientFlagSet: false,
      qlVideoUpload: null
    };
  },
  mixins: [studyDetailMixin],
  components: { dsSlider },
  methods: {
    splitPTagfromContent(text) {
      if (text == null) {
        return;
      }
      return text.replace("<p>", "").replace("</p>", "");
    },
    async getSurveyDetails() {
      try {
        const getSurveyDetails = await this.$api.getStudySurveyDetailsFact(this.studyId);
        this.surveyDetails = getSurveyDetails.body;
        await this.surveyDetails.forEach((survey, surveyIndex) => {
          if (survey.id === this.selectedSurvey.id) {
            let questionCount = 1;
            survey.questions.forEach((question, questionIndex) => {
              this.questionIds[question.id] = question.sequence === 100000 ? "End survey" : questionIndex + 1;
              if (question.type == "CustomScale") {
                if (question.formattedLabels == undefined) {
                  question.formattedLabels = question.labels;
                } else {
                  question.formattedLabels.forEach((element, index) => {
                    question.formattedLabels[index] = this.splitPTagfromContent(element);
                  });
                }
              }
              if (question.type !== "SurveyCompletion") {
                this.surveyDetails[surveyIndex].questions[questionIndex].questionNum = questionCount;
                questionCount += 1;
              }
            });
            if (survey.questions) {
              // Removes unwanted scripts and styles texts
              survey.questions = this.qlVideoUpload.removeHarmfulScriptfromMultipleScreens(survey.questions, "information");
              survey.questions = this.qlVideoUpload.removeHarmfulScriptfromMultipleScreens(survey.questions, "formattedInformation");

              // Replaces old expired urls with new ones
              this.qlVideoUpload.replaceSignedVideoS3Url(survey.questions, "information", survey.participantVideos);
              this.qlVideoUpload.replaceSignedVideoS3Url(survey.questions, "formattedInformation", survey.participantVideos);
            }
            if (survey.questions && survey.questions.length > 0) {
              this.questions = this.processEmailRecipientQuestions(survey.questions);
            }
          }
        });
      } catch (error) {
        this.$toastr.error(error.message || "Unable to fetch the instructions, questions and notifications.");
      }
    },
    processEmailRecipientQuestions(ques) {
      const q = [];
      ques.map(function(value) {
        if (value.isNotifyToEmail) {
          q.push(value.body);
        }
      });
      return q;
    },
    getNotificationEmail() {
      this.$api.getNotificationEmails(this.surveyId, this.studyId).then(
        response => {
          this.emails = response.body;
        },
        error => {
          toastr.error(error.body ? error.body.message : "Error while getting the study details");
        }
      );
    },
    getRecipientFlag() {
      this.$api.getRecipientFlag(this.studyId, this.surveyId).then(
        response => {
          this.isRecipientFlagSet = response.body && response.body.isNotificationRecipient ? response.body.isNotificationRecipient : false;
        },
        error => {
          toastr.error(error.body ? error.body.message : "Error while getting the study details");
        }
      );
    },
    getStudyDetails() {
      this.$api.getStudyDataFact(this.studyId).then(
        response => {
          this.studyData = response.body;
        },
        error => {
          toastr.error(error.body ? error.body.message : "Error while getting the study details");
        }
      );
    },
    displayScreeningMilestones(milestone) {
      if ((milestone !== '' || milestone !== null) && this.studyData.screeningMilestoneAllowed && this.studyData.screeningDuration) {
        milestone -= this.studyData.screeningDuration;
      }
      return milestone;
    }
  },
  mounted() {
    this.qlVideoUpload = new QlVideoUploadService(MODULE_NAME, this.$route.params.studyId, localStorage.getItem("clientId"));
    this.getSurveyDetails();
    this.selectedSurvey.id = this.surveyId;
    this.getNotificationEmail();
    this.getRecipientFlag();
    this.getStudyDetails();
  }
};
</script>

<style scoped>
.inner-card {
  border: none;
  margin: 5px 20px;
  border-radius: 3px !important;
  background-color: #d3dfe4;
}
.content {
  font-size: 12px;
  color: #666669;
}
.header {
  font-size: 12px;
  font-weight: bold;
  color: #666669;
}

.img-style {
  padding-top: 20px;
  height: 300px;
  width: 500px;
  padding-bottom: 20px;
}

.question_body {
  color: #959fa4;
}

.read_only_question {
  border-left: 8px solid #d3dfe4 !important;
  background-color: #f5f8fa;
}

.icon-style {
  color: #57dd7a;
  font-size: 25px;
  padding-top: 20px;
  background-color: #d6f0de;
  padding: 7px;
  padding-bottom: 3px;
  border-radius: 25px;
}

.font-style {
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #3498db;
}

.text-style {
  color: #969090;
  text-align: right;
}

.btn-style {
  margin-top: 10px;
  width: 80px;
  height: 29px;
  margin-bottom: 50px;
}

.custom-btn {
  background: #00baf9;
  color: #fff;
  width: 220px;
}

.pad-top50 {
  padding-top: 50px;
}

.form-control {
  border-radius: 2px;
}

.custom-para {
  padding-bottom: 20px;
  font-size: 20px;
}

.mar-bot20 {
  margin-bottom: 20px;
}

.error-style {
  font-size: 12px;
  color: #ff4848;
  padding-bottom: 5px;
}
.readonly {
  background: #f7f7f7;
}
.btn-disabled {
  color: white;
  background-color: #c6c2c2;
  border: #c6c2c2;
}

.instructionTitle {
  color: #959fa4;
}

.survey-name {
  color: #00baf9;
  font-size: 20px;
}

.pad-left50 {
  padding-left: 50px;
}
.branching_section {
  background-color: #f5f8fa;
  border: solid 1px #d3dfe4;
  padding: 5px 10px;
}
.branching_image {
  max-width: 120px;
  max-height: 120px;
  width: 100%;
  border: 1px solid #ccc;
}
.custom-btn-bg {
  background: linear-gradient(to left, #4b6fe6, #00baf9);
  color: #fff;
}

.mar-bot20 {
  margin-bottom: 20px;
}

.pad-left100 {
  padding-left: 100px;
}

.mar-bot0 {
  margin-bottom: 0px !important;
}

.card {
  border: 1px solid rgba(0, 0, 0, 0.125);
  padding: 15px;
  margin-bottom: 16px;
}

.question_color,
input {
  color: #666769;
  font-size: 14px;
}

.radio-option {
  margin: 0px;
}

.input-text {
  width: 100%;
  border-radius: 2px;
  outline: none;
  border: 1px solid #ccc;
  height: 35px;
  text-indent: 10px;
}

.date-input {
  border-radius: 20px;
  outline: none;
  border: 1px solid #ccc;
  height: 35px;
  text-indent: 10px;
}

.pad-top20 {
  padding-top: 20px;
}

.vertical-vue-slider {
  height: 300px !important;
  width: 4px;
  padding: 11px;
  margin-left: 30px;
}

.horizontal-vue-slider {
  height: 8px;
  width: 100%;
}

.img-size {
  width: 100%;
  height: auto;
  float: none;
  padding: 10px 0 0;
  max-width: 120px;
  max-height: 120px;
}

label {
  font-weight: normal !important;
  color: #959fa4;
}
.wid300 {
  max-width: 300px !important;
}

.img_size {
  width: 100%;
  height: 100%;
  max-width: 100px;
  max-height: 100px;
}

.lang-img-options .col-auto {
  margin-right: 66px !important;
}

.lang-img-options .col-auto:last-of-type {
  margin-right: 0 !important;
}

.language-name {
  color: #959fa4;
}
</style>
